import React, { FunctionComponent, useMemo } from "react";
import { urlBuilderService } from "sprint-common-web";

import { AncillaryContentSubtype } from "../common/types/AncillaryContentType";
import { ContentReferenceType } from "../common/types/ContentReferenceType";
import { ContentSubtype } from "../common/types/ContentSubtype";
import { MovieContentSubtype } from "../common/types/MovieContentSubtype";
import { VersionSummary } from "../common/types/VersionSummary";
import { ProtectedRoute, RouteItem } from "../core/routing/ProtectedRoute";
import { SprintAccessRight } from "../core/security/SprintAccessRight";
import { CloudAssetDetailsPage } from "./details/cloud-asset/CloudAssetDetailsPage";
import { BulkEditCloudAssetContentDashboard } from "./details/cloud-asset/sections/info/content/edit/bulk/BulkEditCloudAssetContentDashboard";
import { EditCloudAssetContentDashboard } from "./details/cloud-asset/sections/info/content/edit/single/EditCloudAssetContentDashboard";
import { EditOnPremAssetContentDashboard } from "./details/on-prem-asset/content/edit/content/EditOnPremAssetContentDashboard";
import { OnPremAssetDetailsPage } from "./details/on-prem-asset/OnPremAssetDetailsPage";
import { AncillaryAssetsListDashboard } from "./list/ancillaries/AncillaryAssetsListDashboard";
import { MovieAssetsListDashboard } from "./list/movies/MovieAssetsListDashboard";
import { TvSeriesAssetsListDashboard } from "./list/tv-series/TvSeriesAssetsListDashboard";
import { AssetsSearchPage } from "./search/AssetsSearchPage";

export const assetsBasePath = "/assets";

const searchAssetsMinAccess = [SprintAccessRight.AssetsRead];

export const onPremAssetsDetailsRoute: RouteItem = {
    path: `${assetsBasePath}/search/:assetType/on-prem/asset/:assetId`,
    component: OnPremAssetDetailsPage,
    accessRights: searchAssetsMinAccess,
    name: "On-Prem Asset Name"
};

export const cloudAssetsDetailsRoute: RouteItem = {
    path: `${assetsBasePath}/search/:assetType/asset/get-details/:assetId`,
    component: CloudAssetDetailsPage,
    accessRights: searchAssetsMinAccess,
    name: "Cloud Asset Name"
};

export const editCloudAssetContentFormRoute: RouteItem = {
    path: `${assetsBasePath}/search/cloud/asset/edit-content/:assetId`,
    component: EditCloudAssetContentDashboard,
    accessRights: [SprintAccessRight.OrdReadWrite],
    name: "Edit cloud asset content"
};

export const bulkEditCloudAssetContentFormRoute: RouteItem = {
    path: `${assetsBasePath}/search/cloud/asset/bulk-edit-content/:assetId`,
    component: BulkEditCloudAssetContentDashboard,
    accessRights: [SprintAccessRight.OrdReadWrite],
    name: "Bulk edit cloud asset content"
};

export const editOnPremAssetContentFormRoute: RouteItem = {
    path: `${assetsBasePath}/search/on-prem/asset/edit-content/:assetId`,
    component: EditOnPremAssetContentDashboard,
    accessRights: [SprintAccessRight.OrdReadWrite],
    name: "Edit on-prem asset content"
};

export const tvSeriesAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/tv-series`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.TV }),
    accessRights: searchAssetsMinAccess,
    name: "Tv Series"
};

export const tvSeriesAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/tv-series/:tvSeriesId`,
    component: TvSeriesAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Tv Series Title"
};

export const movieAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/movies/:movieId`,
    component: MovieAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Movie Title"
};

export const tvSingleAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/tv-singles/:movieId`,
    component: MovieAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Movie Title"
};

export const featureDocumentaryAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/feature-documentaries/:movieId`,
    component: MovieAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Movie Title"
};

export const shortFilmAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/short-films/:movieId`,
    component: MovieAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Movie Title"
};

export const movieAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/movies`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.Movie }),
    accessRights: searchAssetsMinAccess,
    name: "Movies"
};

export const tvSingleAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/tv-singles`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.TvSingle }),
    accessRights: searchAssetsMinAccess,
    name: "Tv Singles"
};

export const featureDocumentaryAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/feature-documentaries`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.FeatureDocumentary }),
    accessRights: searchAssetsMinAccess,
    name: "Feature Documentaries"
};

export const shortFilmAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/short-films`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.ShortFilm }),
    accessRights: searchAssetsMinAccess,
    name: "Short Films"
};

export const advertAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/adverts`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.Advert }),
    accessRights: searchAssetsMinAccess,
    name: "Adverts"
};

export const promoAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/promos`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.Promo }),
    accessRights: searchAssetsMinAccess,
    name: "Promos"
};

export const safetyAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/safeties`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.Safety }),
    accessRights: searchAssetsMinAccess,
    name: "Safeties"
};

export const indentWarningAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/indent-warnings`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.IndentWarning }),
    accessRights: searchAssetsMinAccess,
    name: "Indent Warnings"
};

export const clientSpecificAssetsSearchRoute: RouteItem = {
    path: `${assetsBasePath}/search/client-specific`,
    component: () => AssetsSearchPage({ contentType: ContentSubtype.ClientSpecific }),
    accessRights: searchAssetsMinAccess,
    name: "Client Specific"
};

export const ancillaryAssetsListRoute: RouteItem = {
    path: `${assetsBasePath}/search/ancillaries/:ancillaryId`,
    component: AncillaryAssetsListDashboard,
    accessRights: searchAssetsMinAccess,
    name: "Ancillary Title"
};

export const getAssetsListRoute = (basePath: string, id: string): string => {
    return basePath.slice(0, basePath.lastIndexOf("/")).concat(`/${id}`);
};

export const getCiEditCloudAssetContentPageHref = (assetId: string): string => {
    return editCloudAssetContentFormRoute.path.replace(":assetId", assetId);
};

export const getBulkEditCloudAssetContentPageHref = (assetId: string): string => {
    return bulkEditCloudAssetContentFormRoute.path.replace(":assetId", assetId);
};

export const getCloudAssetDetailsPageHref = (assetId: string): string => {
    return cloudAssetsDetailsRoute.path.replace(":assetType", "cloud").replace(":assetId", assetId);
};

export const getCiEditOnPremAssetContentPageHref = (assetId: string): string => {
    return editOnPremAssetContentFormRoute.path.replace(":assetId", assetId);
};

export const getOnPremAssetDetailsPageHref = (assetId: string): string => {
    return onPremAssetsDetailsRoute.path.replace(":assetId", assetId);
};

export const assetsSearchRoutes = {
    routeItems: [
        cloudAssetsDetailsRoute,
        onPremAssetsDetailsRoute,
        tvSeriesAssetsSearchRoute,
        movieAssetsSearchRoute,
        tvSingleAssetsSearchRoute,
        featureDocumentaryAssetsSearchRoute,
        shortFilmAssetsSearchRoute,
        advertAssetsSearchRoute,
        movieAssetsListRoute,
        tvSingleAssetsListRoute,
        featureDocumentaryAssetsListRoute,
        shortFilmAssetsListRoute,
        promoAssetsSearchRoute,
        tvSeriesAssetsListRoute,
        safetyAssetsSearchRoute,
        ancillaryAssetsListRoute,
        indentWarningAssetsSearchRoute,
        clientSpecificAssetsSearchRoute,
        editCloudAssetContentFormRoute,
        editOnPremAssetContentFormRoute,
        bulkEditCloudAssetContentFormRoute
    ],
    baseRouteNames: ["Assets"]
};

const assetsRoutes = [...assetsSearchRoutes.routeItems];

export const AssetsRoutes: FunctionComponent = () => {
    const routes = useMemo(
        () =>
            assetsRoutes.map((routeItem: RouteItem) => (
                <ProtectedRoute key={routeItem.path} protectedRouteData={routeItem} />
            )),
        [assetsRoutes]
    );

    return <div>{routes}</div>;
};

const urlBuilder = urlBuilderService();

export const getAssetsSearchPageHref = (contentType: ContentSubtype): string => {
    const assetsSearchPath: Record<ContentSubtype, string> = {
        [ContentSubtype.Movie]: movieAssetsSearchRoute.path,
        [ContentSubtype.TvSingle]: tvSingleAssetsSearchRoute.path,
        [ContentSubtype.FeatureDocumentary]: featureDocumentaryAssetsSearchRoute.path,
        [ContentSubtype.ShortFilm]: shortFilmAssetsSearchRoute.path,
        [ContentSubtype.TV]: tvSeriesAssetsSearchRoute.path,
        [ContentSubtype.Advert]: advertAssetsSearchRoute.path,
        [ContentSubtype.Promo]: promoAssetsSearchRoute.path,
        [ContentSubtype.Safety]: safetyAssetsSearchRoute.path,
        [ContentSubtype.IndentWarning]: indentWarningAssetsSearchRoute.path,
        [ContentSubtype.ClientSpecific]: indentWarningAssetsSearchRoute.path
    };

    return urlBuilder.buildUrl(assetsSearchPath[contentType]);
};

export const getAncillaryAssetsListHref = (ancillaryId: string, airlineIds?: string[]): string => {
    const basePath = ancillaryAssetsListRoute.path.replace(":ancillaryId", ancillaryId);

    return urlBuilder.buildUrl(basePath, { airlineIds: airlineIds });
};

export const getMovieAssetsListHref = (
    movieId: string,
    query?: {
        versionTypeIds?: string[];
        distributorIds?: string[];
        brandingIds?: string[];
    }
): string => {
    const basePath = movieAssetsListRoute.path.replace(":movieId", movieId);

    if (!query) {
        return basePath;
    }

    const { versionTypeIds, distributorIds, brandingIds } = query;

    return urlBuilder.buildUrl(basePath, {
        versionTypeIds: versionTypeIds,
        distributorIds: distributorIds,
        brandingIds: brandingIds
    });
};

export const getTvSeriesAssetsListHref = (
    tvSeriesId: string,
    query?: {
        seasonId?: string;
        episodeId?: string;
        versionTypeIds?: string[];
        distributorIds?: string[];
        brandingIds?: string[];
    }
): string => {
    const basePath = tvSeriesAssetsListRoute.path.replace(":tvSeriesId", tvSeriesId);

    if (!query) {
        return basePath;
    }

    const { seasonId, episodeId, versionTypeIds, distributorIds, brandingIds } = query;

    return urlBuilder.buildUrl(basePath, {
        season: seasonId,
        episode: episodeId,
        versionTypes: versionTypeIds,
        distributors: distributorIds,
        brandings: brandingIds
    });
};

interface ContentAssetsListHrefMovie {
    type: ContentReferenceType.Movie;
    movie: { id: string };
    versionSummary: VersionSummary;
}

interface ContentAssetsListHrefTvSeries {
    type: ContentReferenceType.TvSeries;
    tvSeries: { id: string };
    season?: { id: string };
    episode?: { id: string };
    versionSummary: VersionSummary;
}

interface ContentAssetsListHrefAncillary {
    type: ContentReferenceType.Ancillary;
    ancillary: { id: string };
    airline: { id: string };
}

export type ContentAssetsListHrefContent =
    | ContentAssetsListHrefMovie
    | ContentAssetsListHrefTvSeries
    | ContentAssetsListHrefAncillary;

export const getContentAssetsListHref = (content: ContentAssetsListHrefContent): string | undefined => {
    if (content.type === ContentReferenceType.Movie) {
        const { versionSummary } = content;

        return getMovieAssetsListHref(content.movie.id, {
            brandingIds: versionSummary.brandings?.map((branding) => branding.id) ?? [],
            distributorIds: versionSummary.distributors.map((distributor) => distributor.id) ?? [],
            versionTypeIds: [versionSummary.versionType.id]
        });
    }

    if (content.type === ContentReferenceType.TvSeries) {
        const { versionSummary } = content;

        return getTvSeriesAssetsListHref(content.tvSeries.id, {
            brandingIds: versionSummary.brandings?.map((branding) => branding.id) ?? [],
            distributorIds: versionSummary.distributors.map((distributor) => distributor.id) ?? [],
            versionTypeIds: [versionSummary.versionType.id],
            episodeId: content.episode?.id,
            seasonId: content.season?.id
        });
    }

    if (content.type === ContentReferenceType.Ancillary) {
        return getAncillaryAssetsListHref(content.ancillary.id, [content.airline.id]);
    }

    return undefined;
};

export const getCloudMovieDetailsHref = (movieId: string, contentType: MovieContentSubtype): string => {
    return cloudAssetsDetailsRoute.path
        .replace(":assetType", movieTypeUrlLookup[contentType])
        .replace(":assetId", movieId);
};
export const getCloudAncillaryDetailsHref = (ancillaryId: string, contentType: AncillaryContentSubtype): string => {
    return cloudAssetsDetailsRoute.path
        .replace(":assetType", ancillaryTypeUrlLookup[contentType])
        .replace(":assetId", ancillaryId);
};
export const getCloudTvSeriesDetailsHref = (assetId: string): string => {
    return cloudAssetsDetailsRoute.path.replace(":assetType", "tv-series").replace(":assetId", assetId);
};

export const getOnPremMovieDetailsHref = (movieId: string, contentType: MovieContentSubtype): string => {
    return onPremAssetsDetailsRoute.path
        .replace(":assetType", movieTypeUrlLookup[contentType])
        .replace(":assetId", movieId);
};
export const getOnPremAncillaryDetailsHref = (ancillaryId: string, contentType: AncillaryContentSubtype): string => {
    return onPremAssetsDetailsRoute.path
        .replace(":assetType", ancillaryTypeUrlLookup[contentType])
        .replace(":assetId", ancillaryId);
};
export const getOnPremTvSeriesDetailsHref = (assetId: string): string => {
    return onPremAssetsDetailsRoute.path.replace(":assetType", "tv-series").replace(":assetId", assetId);
};

const movieTypeUrlLookup: Record<MovieContentSubtype, string> = {
    [ContentSubtype.Movie]: "movies",
    [ContentSubtype.TvSingle]: "tv-singles",
    [ContentSubtype.FeatureDocumentary]: "feature-documentaries",
    [ContentSubtype.ShortFilm]: "short-films"
};

const ancillaryTypeUrlLookup: Record<AncillaryContentSubtype, string> = {
    [ContentSubtype.Promo]: "promos",
    [ContentSubtype.Safety]: "safeties",
    [ContentSubtype.Advert]: "adverts",
    [ContentSubtype.IndentWarning]: "indent-warnings",
    [ContentSubtype.ClientSpecific]: "client-specific"
};
